import React, { useState } from 'react';
import { sponsorshipService } from '../../../Services/sponsorshipService';
import { Button } from '../../../ui/button';
import { Badge } from '../../../ui/badge';
import { Card, CardHeader, CardTitle, CardContent } from '../../../ui/card';
import { Input } from '../../../ui/input';
import { Label } from '../../../ui/label';
import { Search, TrendingUp, Users, Calendar, AlertCircle } from 'lucide-react';

/**
 * Admin Inventory Page
 * Shows inventory overview and query capabilities
 */
const AdminInventory = () => {
  const [inventoryData, setInventoryData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [queryFilters, setQueryFilters] = useState({
    coverage_scope: 'national',
    territories: [],
    start_date: '',
    end_date: '',
  });

  const handleQuery = async () => {
    if (!queryFilters.start_date || !queryFilters.end_date) {
      setError('Please select both start and end dates');
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      const queryData = {
        coverage_scope: queryFilters.coverage_scope,
        start_date: queryFilters.start_date,
        end_date: queryFilters.end_date,
      };
      
      if (queryFilters.coverage_scope === 'territory' && queryFilters.territories.length > 0) {
        queryData.territories = queryFilters.territories.map(t => ({ state: t }));
      }
      
      const result = await sponsorshipService.inventory.query(queryData);
      setInventoryData(result);
    } catch (err) {
      setError(err.message || 'Failed to query inventory');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setQueryFilters(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Inventory Management</h1>
          <p className="text-muted-foreground mt-1">Query and analyze sponsorship inventory availability</p>
        </div>
      </div>

      {/* Query Filters Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <Search className="h-4 w-4" />
            Inventory Query
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="coverage-scope">Coverage Scope</Label>
                <select
                  id="coverage-scope"
                  className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  value={queryFilters.coverage_scope}
                  onChange={(e) => handleFilterChange('coverage_scope', e.target.value)}
                >
                  <option value="national">National</option>
                  <option value="territory">Territory</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="start-date">Start Date</Label>
                <Input
                  id="start-date"
                  type="date"
                  value={queryFilters.start_date}
                  onChange={(e) => handleFilterChange('start_date', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end-date">End Date</Label>
                <Input
                  id="end-date"
                  type="date"
                  value={queryFilters.end_date}
                  onChange={(e) => handleFilterChange('end_date', e.target.value)}
                />
              </div>
            </div>
            
            {queryFilters.coverage_scope === 'territory' && (
              <div className="space-y-2">
                <Label htmlFor="territories">Territories (comma-separated states)</Label>
                <Input
                  id="territories"
                  placeholder="e.g., CA, NY, TX"
                  value={queryFilters.territories.join(', ')}
                  onChange={(e) => handleFilterChange('territories', e.target.value.split(',').map(c => c.trim()).filter(c => c))}
                />
              </div>
            )}
            
            <div className="flex gap-3">
              <Button onClick={handleQuery} disabled={loading}>
                {loading ? 'Querying...' : 'Query Inventory'}
              </Button>
              <Button 
                variant="outline"
                onClick={() => setQueryFilters({
                  coverage_scope: 'national',
                  territories: [],
                  start_date: '',
                  end_date: '',
                })}
              >
                Clear Filters
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error Display */}
      {error && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-4 w-4" />
              <p className="text-sm">{error}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Loading State */}
      {loading && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-center py-12">
              <div className="text-muted-foreground">Querying inventory...</div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {inventoryData && !loading && (
        <>
          {/* Summary Metrics */}
          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Users className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{inventoryData.eligible_users_count?.toLocaleString() || 0}</p>
                    <p className="text-sm text-muted-foreground">Eligible Users</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <TrendingUp className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{inventoryData.total_capacity_weeks?.toLocaleString() || 0}</p>
                    <p className="text-sm text-muted-foreground">Total Capacity</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <Calendar className="h-6 w-6 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{inventoryData.allocated_weeks?.toLocaleString() || 0}</p>
                    <p className="text-sm text-muted-foreground">Allocated Weeks</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Calendar className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{inventoryData.remaining_weeks?.toLocaleString() || 0}</p>
                    <p className="text-sm text-muted-foreground">Available Weeks</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Utilization Rate */}
          <Card>
            <CardHeader>
              <CardTitle>Utilization Overview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Current Utilization Rate</span>
                  <span className="text-2xl font-bold">{inventoryData.utilization_percent?.toFixed(1) || 0}%</span>
                </div>
                <div className="w-full bg-secondary rounded-full h-4 overflow-hidden">
                  <div 
                    className="h-full bg-primary transition-all duration-300"
                    style={{ width: `${inventoryData.utilization_percent || 0}%` }}
                  />
                </div>
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>0%</span>
                  <span>50%</span>
                  <span>100%</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Query Details */}
          <Card>
            <CardHeader>
              <CardTitle>Query Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <p className="text-sm text-muted-foreground">Coverage Scope</p>
                  <p className="font-medium capitalize">{inventoryData.coverage_scope}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Date Range</p>
                  <p className="font-medium">{inventoryData.start_date} to {inventoryData.end_date}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Weeks in Window</p>
                  <p className="font-medium">{inventoryData.weeks_in_window} weeks</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Utilization</p>
                  <Badge variant={inventoryData.utilization_percent > 80 ? 'warning' : 'success'}>
                    {inventoryData.utilization_percent?.toFixed(1)}% utilized
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default AdminInventory;
